/**
 * Anonymous Authentication Utility
 *
 * Implements zero-knowledge authentication where:
 * - Passphrase deterministically generates a UUID via PBKDF2/SHA-256
 * - No server-side authentication or email required
 * - Same passphrase = same UUID (cross-device support)
 * - UUID stored only in browser localStorage
 * - Complete anonymity with no IP logging
 */

import { generateBIP39Passphrase, isBIP39Passphrase, calculateBIP39Entropy } from './bip39-wordlist'

/**
 * Derives a deterministic UUID from a passphrase using PBKDF2 with SHA-256
 *
 * @param passphrase - User's passphrase (should be at least 12 characters)
 * @returns Promise resolving to a valid UUID v4 string
 *
 * @example
 * const uuid = await deriveUUIDFromPassphrase('my-secret-passphrase-123')
 * // Returns: "550e8400-e29b-41d4-a716-446655440000" (same UUID for same passphrase)
 */
export async function deriveUUIDFromPassphrase(passphrase: string): Promise<string> {
  // Validate passphrase length
  if (passphrase.length < 12) {
    throw new Error('Passphrase must be at least 12 characters long')
  }

  // Fixed salt for deterministic UUID generation
  // This ensures same passphrase always produces same UUID
  const salt = 'solidaritynode-anonymous-auth-v1'

  // Encode passphrase and salt
  const encoder = new TextEncoder()
  const passphraseBuffer = encoder.encode(passphrase)
  const saltBuffer = encoder.encode(salt)

  // Import passphrase as key material
  const keyMaterial = await crypto.subtle.importKey(
    'raw',
    passphraseBuffer,
    'PBKDF2',
    false,
    ['deriveBits']
  )

  // Derive 128 bits (16 bytes) using PBKDF2
  // 100,000 iterations for security (recommended minimum)
  const derivedBits = await crypto.subtle.deriveBits(
    {
      name: 'PBKDF2',
      salt: saltBuffer,
      iterations: 100000,
      hash: 'SHA-256'
    },
    keyMaterial,
    128 // 128 bits = 16 bytes for UUID
  )

  // Convert to byte array
  const bytes = new Uint8Array(derivedBits)

  // Format as UUID v4
  // UUID v4 format: xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx
  // where x is any hexadecimal digit and y is one of 8, 9, A, or B

  // Set version (4) and variant (RFC 4122)
  bytes[6] = (bytes[6] & 0x0f) | 0x40 // Version 4
  bytes[8] = (bytes[8] & 0x3f) | 0x80 // Variant RFC 4122

  // Convert to hex string with dashes
  const hex = Array.from(bytes)
    .map(b => b.toString(16).padStart(2, '0'))
    .join('')

  return [
    hex.substring(0, 8),
    hex.substring(8, 12),
    hex.substring(12, 16),
    hex.substring(16, 20),
    hex.substring(20, 32)
  ].join('-')
}

/**
 * Validates if a passphrase meets minimum security requirements
 *
 * @param passphrase - Passphrase to validate
 * @returns Object with isValid flag and optional error message
 */
export function validatePassphrase(passphrase: string): {
  isValid: boolean
  error?: string
} {
  if (!passphrase) {
    return { isValid: false, error: 'Η φράση πρόσβασης είναι υποχρεωτική' }
  }

  if (passphrase.length < 12) {
    return {
      isValid: false,
      error: 'Η φράση πρόσβασης πρέπει να έχει τουλάχιστον 12 χαρακτήρες'
    }
  }

  if (passphrase.length > 128) {
    return {
      isValid: false,
      error: 'Η φράση πρόσβασης δεν μπορεί να υπερβαίνει τους 128 χαρακτήρες'
    }
  }

  // Check for common weak passphrases
  const weakPassphrases = [
    '123456789012',
    'password1234',
    'qwertyuiop12',
    'abcdefghijkl'
  ]

  if (weakPassphrases.includes(passphrase.toLowerCase())) {
    return {
      isValid: false,
      error: 'Παρακαλώ χρησιμοποιήστε μια πιο ισχυρή φράση πρόσβασης'
    }
  }

  return { isValid: true }
}

/**
 * Generates a random passphrase suggestion using BIP39 wordlist
 *
 * @returns A random 16-word BIP39 passphrase (176 bits of entropy)
 *
 * @example
 * generatePassphraseSuggestion()
 * // Returns: "abandon ability across action adapt address advance advice..."
 */
export function generatePassphraseSuggestion(): string {
  return generateBIP39Passphrase(16)
}

/**
 * Check passphrase entropy and provide warnings
 *
 * @param passphrase - Passphrase to check
 * @returns Strength assessment and optional warning
 */
export function checkPassphraseEntropy(passphrase: string): {
  strength: 'weak' | 'medium' | 'strong'
  warning?: string
  entropy?: number
} {
  const words = passphrase.trim().split(/[\s-]+/)

  // Check if it's a BIP39 passphrase
  const isBIP39 = isBIP39Passphrase(passphrase)

  if (isBIP39) {
    const entropy = calculateBIP39Entropy(words.length)

    if (words.length < 12) {
      return {
        strength: 'medium',
        warning: '⚠️ Για μέγιστη ασφάλεια, χρησιμοποιήστε τουλάχιστον 12 λέξεις BIP39',
        entropy
      }
    }

    if (words.length < 16) {
      return {
        strength: 'medium',
        warning: 'ℹ️ Καλή ασφάλεια. Για μέγιστη προστασία, συνιστούμε 16 λέξεις.',
        entropy
      }
    }

    return { strength: 'strong', entropy }
  }

  // Check for repeated words
  const uniqueWords = new Set(words)
  if (uniqueWords.size < words.length) {
    return {
      strength: 'weak',
      warning: '⚠️ Επαναλαμβανόμενες λέξεις μειώνουν την ασφάλεια'
    }
  }

  // Short passphrase
  if (passphrase.length < 20) {
    return {
      strength: 'weak',
      warning: '⚠️ Πολύ σύντομη φράση. Συνιστούμε χρήση του αυτόματου generator.'
    }
  }

  return {
    strength: 'medium',
    warning: 'ℹ️ Για μέγιστη ασφάλεια, χρησιμοποιήστε το αυτόματο generator με 16 λέξεις BIP39.'
  }
}

/**
 * Local storage keys for anonymous auth
 */
export const STORAGE_KEYS = {
  USER_UUID: 'solidarity_user_uuid',
  SESSION_CREATED: 'solidarity_session_created'
} as const

/**
 * Saves user UUID to localStorage
 *
 * @param uuid - User's UUID to save
 */
export function saveUserUUID(uuid: string): void {
  localStorage.setItem(STORAGE_KEYS.USER_UUID, uuid)
  localStorage.setItem(STORAGE_KEYS.SESSION_CREATED, new Date().toISOString())
}

/**
 * Retrieves user UUID from localStorage
 *
 * @returns UUID string or null if not found
 */
export function getUserUUID(): string | null {
  return localStorage.getItem(STORAGE_KEYS.USER_UUID)
}

/**
 * Clears user session from localStorage
 */
export function clearUserSession(): void {
  localStorage.removeItem(STORAGE_KEYS.USER_UUID)
  localStorage.removeItem(STORAGE_KEYS.SESSION_CREATED)
}

/**
 * Checks if user has an active session
 *
 * @returns true if UUID exists in localStorage
 */
export function hasActiveSession(): boolean {
  return getUserUUID() !== null
}

/**
 * Gets session metadata
 *
 * @returns Object with UUID and creation date, or null
 */
export function getSessionMetadata(): {
  uuid: string
  createdAt: Date
} | null {
  const uuid = getUserUUID()
  const createdAt = localStorage.getItem(STORAGE_KEYS.SESSION_CREATED)

  if (!uuid || !createdAt) {
    return null
  }

  return {
    uuid,
    createdAt: new Date(createdAt)
  }
}
